import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  EstabelecimentoDataService,
  EstabelecimentoService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import {
  DeviceService,
  FormFieldComponent,
  NMultiselectComponent,
  PhoneInputComponent,
} from '@nomoh-monorepo/shared-common-ui';
import { fechamentoMaiorQueAbertura } from '@nomoh-monorepo/shared-angular-util-tools';
import {
  DiasSemanaOptions,
  ehPaisBrasil,
  EstadosConst,
  IEstabelecimento,
  IImagem,
  IPaisOption,
  listarPaises,
  NichoEstabelecimentoEnum,
  NichoEstabelecimentoOptions,
  normalizarCodigoPais,
  PAIS_BRASIL,
} from '@nomoh-monorepo/shared-types';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lib-etapa-estabelecimento',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    InputMaskModule, // mantido para máscara de CEP
    ButtonModule,
    SelectButtonModule,
    DatePickerModule,
    MultiSelectModule,
    SelectModule,
    CheckboxModule,
    FormFieldComponent,
    NMultiselectComponent,
    PhoneInputComponent,
    TranslateModule,
  ],
  templateUrl: './etapa-estabelecimento.component.html',
  styleUrls: ['./etapa-estabelecimento.component.scss'],
})
export class EtapaEstabelecimentoComponent {
  @Output() proximaEtapa = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private estabelecimentoService = inject(EstabelecimentoService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private notificacaoService = inject(NotificacaoService);
  private destroyRef = inject(DestroyRef);
  private translateService = inject(TranslateService);
  readonly deviceService = inject(DeviceService);
  private readonly HORARIO_STEP_MINUTOS = 30;

  formEstabelecimento: FormGroup;
  salvando = false;
  buscandoCep = false;

  // ViewChild para os inputs de arquivo
  @ViewChild('fileInputCapa') fileInputCapa!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInputPerfil') fileInputPerfil!: ElementRef<HTMLInputElement>;

  // Signals para upload de imagens - Capa
  uploadingCapa = signal<boolean>(false);
  uploadProgressCapa = signal<number>(0);
  dragActiveCapa = signal<boolean>(false);
  errorCapa = signal<boolean>(false);
  successCapa = signal<boolean>(false);

  // Signals para upload de imagens - Perfil
  uploadingPerfil = signal<boolean>(false);
  uploadProgressPerfil = signal<number>(0);
  dragActivePerfil = signal<boolean>(false);
  errorPerfil = signal<boolean>(false);
  successPerfil = signal<boolean>(false);

  // Preview das imagens e arquivos
  previewCapa: string | null = null;
  previewPerfil: string | null = null;
  arquivoCapa: File | null = null;
  arquivoPerfil: File | null = null;

  // Imagens modificadas para salvar
  imagensModificadas: IImagem[] = [];

  private readonly imagemConfig = {
    capa: { maxWidth: 1600, maxHeight: 640, maxSizeKB: 900 },
    perfil: { maxWidth: 600, maxHeight: 600, maxSizeKB: 250 },
  } as const;

  // Opções
  get simNaoOptions() {
    return [
      { label: this.translateService.instant('GLOBAL.SIM'), value: true },
      { label: this.translateService.instant('GLOBAL.NAO'), value: false },
    ];
  }

  diasSemana = DiasSemanaOptions.map((dia) => ({
    id: dia.id,
    label: dia.nome,
  }));

  estados: string[] = EstadosConst.sigla;
  paises: IPaisOption[] = listarPaises();
  nichos = NichoEstabelecimentoOptions;
  private ignorarLimpezaPais = false;
  private paisAnterior = PAIS_BRASIL;

  constructor() {
    this.formEstabelecimento = this.fb.group(
      {
        // Informações Básicas
        nome: ['', Validators.required],
        telefone: ['', Validators.required],
        nicho: [NichoEstabelecimentoEnum.BELEZA_ESTETICA, Validators.required],
        abreFeriado: [false],
        redeSocial: [''],
        website: [''],

        // Horários
        horaAbertura: [
          this.deviceService.$isMobile() ? '08:00' : this.criarHorario(8, 0),
          Validators.required,
        ],
        horaFechamento: [
          this.deviceService.$isMobile() ? '18:00' : this.criarHorario(18, 0),
          Validators.required,
        ],
        diasFuncionamento: [[], Validators.required],

        // Endereço
        cep: ['', Validators.required],
        logradouro: ['', Validators.required],
        numero: ['', Validators.required],
        complemento: [''],
        bairro: ['', Validators.required],
        cidade: ['', Validators.required],
        estado: ['', Validators.required],
        pais: [PAIS_BRASIL, Validators.required],
      },
      { validators: fechamentoMaiorQueAbertura }
    );

    this.formEstabelecimento
      .get('pais')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pais) => this.aplicarRegrasPais(pais));

    this.sincronizarCampoHorario('horaAbertura');
    this.sincronizarCampoHorario('horaFechamento');

    this.preencherFormulario();
  }

  aoSelecionarPais(pais: unknown): void {
    this.aplicarRegrasPais(pais);
  }

  ehEnderecoBrasil(): boolean {
    return this.extrairCodigoPais(this.formEstabelecimento?.get('pais')?.value) === PAIS_BRASIL;
  }

  private extrairCodigoPais(pais: unknown): string {
    if (pais && typeof pais === 'object') {
      if ('codigo' in pais) {
        return normalizarCodigoPais(String((pais as { codigo: unknown }).codigo ?? ''));
      }
      if ('value' in pais) {
        return this.extrairCodigoPais((pais as { value: unknown }).value);
      }
    }

    return normalizarCodigoPais(typeof pais === 'string' ? pais : null);
  }

  private aplicarRegrasPais(pais: unknown): void {
    const codigo = this.extrairCodigoPais(pais);
    const controlePais = this.formEstabelecimento.get('pais');
    if (controlePais && controlePais.value !== codigo) {
      controlePais.setValue(codigo, { emitEvent: false });
    }

    const brasil = codigo === PAIS_BRASIL;
    const mudouTipo = ehPaisBrasil(this.paisAnterior) !== brasil;
    this.paisAnterior = codigo;

    const bairro = this.formEstabelecimento.get('bairro');
    const complemento = this.formEstabelecimento.get('complemento');
    const estado = this.formEstabelecimento.get('estado');
    const cep = this.formEstabelecimento.get('cep');

    if (brasil) {
      bairro?.setValidators([Validators.required, Validators.maxLength(100)]);
      estado?.setValidators([Validators.required]);
      cep?.setValidators([Validators.required, Validators.maxLength(20)]);
    } else {
      bairro?.clearValidators();
      estado?.setValidators([Validators.required, Validators.maxLength(100)]);
      cep?.setValidators([Validators.required, Validators.maxLength(20)]);
    }

    if (!this.ignorarLimpezaPais && mudouTipo) {
      if (brasil) {
        if (!this.estados.includes(String(estado?.value ?? ''))) {
          estado?.setValue('', { emitEvent: false });
        }
        const digitos = String(cep?.value ?? '').replace(/\D/g, '');
        if (digitos.length !== 8) {
          cep?.setValue('', { emitEvent: false });
        }
        if (bairro?.value == null) {
          bairro?.setValue('', { emitEvent: false });
        }
      } else {
        bairro?.setValue(null, { emitEvent: false });
        complemento?.setValue(null, { emitEvent: false });
        if (this.estados.includes(String(estado?.value ?? ''))) {
          estado?.setValue('', { emitEvent: false });
        }
        const digitos = String(cep?.value ?? '').replace(/\D/g, '');
        if (digitos.length === 8) {
          cep?.setValue('', { emitEvent: false });
        }
      }
    }

    bairro?.updateValueAndValidity({ emitEvent: false });
    complemento?.updateValueAndValidity({ emitEvent: false });
    estado?.updateValueAndValidity({ emitEvent: false });
    cep?.updateValueAndValidity({ emitEvent: false });
  }

  private preencherFormulario() {
    const estabelecimento =
      this.estabelecimentoDataService.$estabelecimentoAtual();

    if (estabelecimento) {
      const pais = normalizarCodigoPais(estabelecimento.endereco?.pais);
      this.ignorarLimpezaPais = true;
      this.formEstabelecimento.patchValue({
        nome: estabelecimento.nome,
        telefone: estabelecimento.telefone,
        nicho: estabelecimento.nicho || NichoEstabelecimentoEnum.BELEZA_ESTETICA,
        abreFeriado: estabelecimento.abreFeriado,
        redeSocial: estabelecimento.redeSocial || '',
        website: estabelecimento.website || '',
        horaAbertura: this.deviceService.$isMobile()
          ? estabelecimento.horaAbertura || null
          : this.parseTimeString(estabelecimento.horaAbertura),
        horaFechamento: this.deviceService.$isMobile()
          ? estabelecimento.horaFechamento || null
          : this.parseTimeString(estabelecimento.horaFechamento),
        diasFuncionamento:
          estabelecimento.diasFuncionamento?.map((d) => d.diaId) || [],
        cep: estabelecimento.endereco?.codPostal,
        logradouro: estabelecimento.endereco?.logradouro,
        numero: estabelecimento.endereco?.numero,
        complemento: ehPaisBrasil(pais)
          ? estabelecimento.endereco?.complemento
          : null,
        bairro: ehPaisBrasil(pais) ? estabelecimento.endereco?.bairro : null,
        cidade: estabelecimento.endereco?.cidade,
        estado: estabelecimento.endereco?.estado,
        pais,
      });
      this.aplicarRegrasPais(pais);
      this.ignorarLimpezaPais = false;

      if (estabelecimento.imagens && estabelecimento.imagens.length > 0) {
        const imgCapa = estabelecimento.imagens.find(
          (img) => img.token === 'FOTO_CAPA'
        );
        const imgPerfil = estabelecimento.imagens.find(
          (img) => img.token === 'FOTO_PERFIL'
        );

        if (imgCapa && imgCapa.conteudo) {
          this.previewCapa = `data:${imgCapa.tipo || 'image/jpeg'};base64,${
            imgCapa.conteudo
          }`;
          this.imagensModificadas.push(imgCapa);
        }
        if (imgPerfil && imgPerfil.conteudo) {
          this.previewPerfil = `data:${imgPerfil.tipo || 'image/jpeg'};base64,${
            imgPerfil.conteudo
          }`;
          this.imagensModificadas.push(imgPerfil);
        }
      }
    }
  }

  private parseTimeString(timeStr?: string): Date | null {
    if (!timeStr) return null;
    const partes = timeStr.split(':');
    if (partes.length >= 2) {
      const horas = parseInt(partes[0], 10);
      const minutos = parseInt(partes[1], 10);
      if (!isNaN(horas) && !isNaN(minutos)) {
        return this.criarHorario(horas, minutos);
      }
    }
    return null;
  }

  onTelefoneChange(value: string): void {
    const ctrl = this.formEstabelecimento.get('telefone');
    ctrl?.setValue(value);
    ctrl?.markAsDirty();
    ctrl?.markAsTouched();
  }

  async buscarCep(): Promise<void> {
    const cep = this.formEstabelecimento.get('cep')?.value?.replace(/\D/g, '');

    if (!cep || cep.length !== 8) {
      return;
    }

    try {
      this.buscandoCep = true;
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();

      if (data.erro) {
        this.notificacaoService.notificacaoWarn(
          this.translateService.instant('PRIMEIRO_ACESSO.NOTIFICACAO_CEP_NAO_ENCONTRADO')
        );
        return;
      }

      this.formEstabelecimento.patchValue({
        logradouro: data.logradouro,
        bairro: data.bairro,
        cidade: data.localidade,
        estado: data.uf,
      });
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      this.notificacaoService.notificacaoWarn(
        this.translateService.instant('PRIMEIRO_ACESSO.NOTIFICACAO_ERRO_CEP')
      );
    } finally {
      this.buscandoCep = false;
    }
  }

  async salvar(): Promise<void> {
    if (this.formEstabelecimento.invalid) {
      this.formEstabelecimento.markAllAsTouched();
      if (this.formEstabelecimento.errors?.['fechamentoMenorQueAbertura']) {
        this.notificacaoService.notificacaoWarn(
          this.translateService.instant('PRIMEIRO_ACESSO.NOTIFICACAO_ERRO_FECHAMENTO')
        );
      }
      return;
    }

    try {
      this.salvando = true;
      const formValue = this.formEstabelecimento.value;

      // Formatar horários
      const horaAbertura = formValue.horaAbertura
        ? this.formatTime(formValue.horaAbertura)
        : '';
      const horaFechamento = formValue.horaFechamento
        ? this.formatTime(formValue.horaFechamento)
        : '';

      const tipoFluxoId =
        await this.estabelecimentoService.buscarTiposFluxoAtendimento(
          'AGENDAMENTO'
        );

      const imagensFinais = this.imagensModificadas.filter(
        (img) => img.conteudo.trim() !== ''
      );

      const estabelecimento = {
        nome: formValue.nome,
        telefone: formValue.telefone || undefined,
        abreFeriado: formValue.abreFeriado,
        redeSocial: formValue.redeSocial || null,
        website: formValue.website || null,
        horaAbertura,
        horaFechamento,
        diasFuncionamento: formValue.diasFuncionamento.map(
          (diaId: number | string) => ({
            diaId: Number(diaId),
          })
        ),
        endereco: {
          codPostal: this.ehEnderecoBrasil()
            ? formValue.cep?.replace(/\D/g, '')
            : String(formValue.cep ?? '').trim(),
          logradouro: formValue.logradouro,
          numero: formValue.numero,
          complemento: this.ehEnderecoBrasil()
            ? formValue.complemento || null
            : null,
          bairro: this.ehEnderecoBrasil() ? formValue.bairro : null,
          cidade: formValue.cidade,
          estado: formValue.estado,
          pais: normalizarCodigoPais(formValue.pais),
        },
        status: true,
        tipoFluxoId: tipoFluxoId,
        imagens: imagensFinais,
      };

      const estabelecimentoAtual =
        this.estabelecimentoDataService.$estabelecimentoAtual();

      if (estabelecimentoAtual && estabelecimentoAtual.id) {
        const estabelecimentoAtualizado = {
          ...estabelecimento,
          id: estabelecimentoAtual.id,
          endereco: {
            ...estabelecimento.endereco,
            id: estabelecimentoAtual.endereco?.id,
          },
        };
        await this.estabelecimentoService.atualizar(
          estabelecimentoAtual.id,
          estabelecimentoAtualizado as Partial<IEstabelecimento>
        );
        await this.estabelecimentoService.findById(estabelecimentoAtual.id);
      } else {
        const id = await this.estabelecimentoService.criar(estabelecimento);
        await this.estabelecimentoService.findById(id);
      }

      this.proximaEtapa.emit();
    } catch (error) {
      console.error('Erro ao salvar estabelecimento:', error);

      this.notificacaoService.notificacaoError(
        this.translateService.instant('PRIMEIRO_ACESSO.NOTIFICACAO_ERRO_SALVAR')
      );
    } finally {
      this.salvando = false;
    }
  }

  private formatTime(date: Date | string): string {
    if (typeof date === 'string') {
      // "HH:mm" from native time input — already correct format
      return date.substring(0, 5);
    }
    if (!(date instanceof Date)) {
      return '';
    }

    const normalizado = this.arredondarParaSlot(date);
    const hours = normalizado.getHours().toString().padStart(2, '0');
    const minutes = normalizado.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private criarHorario(horas: number, minutos: number): Date {
    const base = new Date();
    base.setHours(horas, minutos, 0, 0);
    return base;
  }

  private sincronizarCampoHorario(campo: 'horaAbertura' | 'horaFechamento') {
    const control = this.formEstabelecimento.get(campo);
    if (!control) {
      return;
    }

    control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((valor: Date | null) => {
        if (!(valor instanceof Date)) {
          return;
        }

        const arredondado = this.arredondarParaSlot(valor);
        if (valor.getTime() !== arredondado.getTime()) {
          control.setValue(arredondado, { emitEvent: false });
        }
      });
  }

  private arredondarParaSlot(valor: Date): Date {
    const minutosTotais = valor.getHours() * 60 + valor.getMinutes();
    const slot = Math.max(
      0,
      Math.min(47, Math.round(minutosTotais / this.HORARIO_STEP_MINUTOS))
    );
    const minutosFinal = slot * this.HORARIO_STEP_MINUTOS;
    const horas = Math.floor(minutosFinal / 60);
    const minutos = minutosFinal % 60;

    const ajustado = new Date(valor);
    ajustado.setHours(horas, minutos, 0, 0);
    return ajustado;
  }

  get isFormValid(): boolean {
    return this.formEstabelecimento.valid;
  }

  get horariosInvalidos(): boolean {
    return !!(
      this.formEstabelecimento.errors?.['fechamentoMenorQueAbertura'] &&
      (this.formEstabelecimento.get('horaAbertura')?.dirty ||
        this.formEstabelecimento.get('horaFechamento')?.dirty)
    );
  }

  // ========================================
  // MÉTODOS DE UPLOAD DE IMAGENS
  // ========================================

  selectImage(tipo: 'capa' | 'perfil'): void {
    if (tipo === 'capa') {
      this.fileInputCapa?.nativeElement?.click();
    } else {
      this.fileInputPerfil?.nativeElement?.click();
    }
  }

  onFileSelect(tipo: 'capa' | 'perfil', event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.processarArquivo(tipo, file);
    }
  }

  onDragOver(event: DragEvent, tipo: 'capa' | 'perfil'): void {
    event.preventDefault();
    event.stopPropagation();
    if (tipo === 'capa') {
      this.dragActiveCapa.set(true);
    } else {
      this.dragActivePerfil.set(true);
    }
  }

  onDragLeave(event: DragEvent, tipo: 'capa' | 'perfil'): void {
    event.preventDefault();
    event.stopPropagation();
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      if (tipo === 'capa') {
        this.dragActiveCapa.set(false);
      } else {
        this.dragActivePerfil.set(false);
      }
    }
  }

  onDrop(event: DragEvent, tipo: 'capa' | 'perfil'): void {
    event.preventDefault();
    event.stopPropagation();
    if (tipo === 'capa') {
      this.dragActiveCapa.set(false);
    } else {
      this.dragActivePerfil.set(false);
    }

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processarArquivo(tipo, files[0]);
    }
  }

  removeImage(tipo: 'capa' | 'perfil'): void {
    if (tipo === 'capa') {
      this.previewCapa = null;
      this.arquivoCapa = null;
      if (this.fileInputCapa?.nativeElement) {
        this.fileInputCapa.nativeElement.value = '';
      }
      this.resetarEstadosCapa();
      this.marcarImagemParaRemocao('FOTO_CAPA');
    } else {
      this.previewPerfil = null;
      this.arquivoPerfil = null;
      if (this.fileInputPerfil?.nativeElement) {
        this.fileInputPerfil.nativeElement.value = '';
      }
      this.resetarEstadosPerfil();
      this.marcarImagemParaRemocao('FOTO_PERFIL');
    }
  }

  private marcarImagemParaRemocao(token: 'FOTO_CAPA' | 'FOTO_PERFIL'): void {
    this.imagensModificadas = this.imagensModificadas.filter(
      (img) => img.token !== token
    );
  }

  private processarArquivo(tipo: 'capa' | 'perfil', file: File): void {
    this.resetarEstados(tipo);

    if (!file.type.startsWith('image/')) {
      this.mostrarErro(tipo, 'Por favor, selecione apenas arquivos de imagem.');
      return;
    }
    const maxSize = tipo === 'capa' ? 5 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      const maxSizeMB = tipo === 'capa' ? '5MB' : '5MB';
      this.mostrarErro(tipo, `A imagem deve ter no máximo ${maxSizeMB}.`);
      return;
    }
    this.iniciarUploadSimulado(tipo, file);
  }

  private iniciarUploadSimulado(tipo: 'capa' | 'perfil', file: File): void {
    if (tipo === 'capa') {
      this.uploadingCapa.set(true);
      this.uploadProgressCapa.set(0);
    } else {
      this.uploadingPerfil.set(true);
      this.uploadProgressPerfil.set(0);
    }

    const interval = setInterval(() => {
      const currentProgress =
        tipo === 'capa'
          ? this.uploadProgressCapa()
          : this.uploadProgressPerfil();
      const newProgress = Math.min(currentProgress + Math.random() * 25, 100);

      if (tipo === 'capa') {
        this.uploadProgressCapa.set(newProgress);
      } else {
        this.uploadProgressPerfil.set(newProgress);
      }

      if (newProgress >= 100) {
        clearInterval(interval);
        this.finalizarUpload(tipo, file);
      }
    }, 100);
  }

  private finalizarUpload(tipo: 'capa' | 'perfil', file: File): void {
    const reader = new FileReader();
    reader.onload = async (e: ProgressEvent<FileReader>) => {
      const base64Result = e.target?.result as string;
      const base64Comprimido = await this.comprimirImagem(
        base64Result,
        file.type,
        tipo
      );

      if (tipo === 'capa') {
        this.previewCapa = base64Comprimido;
        this.arquivoCapa = file;
        this.uploadingCapa.set(false);
        this.successCapa.set(true);
        setTimeout(() => this.successCapa.set(false), 2000);
      } else {
        this.previewPerfil = base64Comprimido;
        this.arquivoPerfil = file;
        this.uploadingPerfil.set(false);
        this.successPerfil.set(true);
        setTimeout(() => this.successPerfil.set(false), 2000);
      }

      this.prepararImagemParaSalvar(
        tipo,
        base64Comprimido,
        file.name,
        file.type
      );
    };
    reader.readAsDataURL(file);
  }

  private prepararImagemParaSalvar(
    tipo: 'capa' | 'perfil',
    base64: string,
    nomeArquivo: string,
    type: string
  ): void {
    const token = tipo === 'capa' ? 'FOTO_CAPA' : 'FOTO_PERFIL';
    const base64Limpo = base64.split(',')[1];

    this.imagensModificadas = this.imagensModificadas.filter(
      (img) => img.token !== token
    );

    this.imagensModificadas.push({
      id: '',
      estabelecimentoId: '',
      nome: nomeArquivo,
      conteudo: base64Limpo,
      tipo: type,
      token: token,
    });
  }

  private mostrarErro(tipo: 'capa' | 'perfil', mensagem: string): void {
    if (tipo === 'capa') {
      this.errorCapa.set(true);
      setTimeout(() => this.errorCapa.set(false), 3000);
    } else {
      this.errorPerfil.set(true);
      setTimeout(() => this.errorPerfil.set(false), 3000);
    }
    this.notificacaoService.notificacaoWarn(mensagem);
  }

  private resetarEstados(tipo: 'capa' | 'perfil'): void {
    if (tipo === 'capa') {
      this.resetarEstadosCapa();
    } else {
      this.resetarEstadosPerfil();
    }
  }

  private resetarEstadosCapa(): void {
    this.uploadingCapa.set(false);
    this.uploadProgressCapa.set(0);
    this.dragActiveCapa.set(false);
    this.errorCapa.set(false);
    this.successCapa.set(false);
  }

  private resetarEstadosPerfil(): void {
    this.uploadingPerfil.set(false);
    this.uploadProgressPerfil.set(0);
    this.dragActivePerfil.set(false);
    this.errorPerfil.set(false);
    this.successPerfil.set(false);
  }

  private async comprimirImagem(
    base64: string,
    mimeType: string,
    tipo: 'capa' | 'perfil'
  ): Promise<string> {
    const config = this.imagemConfig[tipo];
    const tamanhoAtual = this.calcularTamanhoBase64(base64);
    const limiteBytes = config.maxSizeKB * 1024;

    if (tamanhoAtual <= limiteBytes) {
      return base64;
    }

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const { width, height } = this.calcularDimensoesAlvo(
          img.width,
          img.height,
          config.maxWidth,
          config.maxHeight
        );

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(base64);
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        const destino = mimeType === 'image/png' ? 'image/png' : 'image/jpeg';
        let qualidade: number | undefined =
          destino === 'image/png' ? undefined : 0.85;
        let dataUrl = canvas.toDataURL(destino, qualidade);

        while (
          destino !== 'image/png' &&
          this.calcularTamanhoBase64(dataUrl) > limiteBytes &&
          qualidade &&
          qualidade > 0.4
        ) {
          qualidade = qualidade - 0.1;
          dataUrl = canvas.toDataURL(destino, qualidade);
        }

        resolve(dataUrl);
      };
      img.src = base64;
    });
  }

  private calcularDimensoesAlvo(
    larguraOriginal: number,
    alturaOriginal: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number; height: number } {
    let width = larguraOriginal;
    let height = alturaOriginal;
    const proporcao = width / height;

    if (width > maxWidth) {
      width = maxWidth;
      height = Math.round(width / proporcao);
    }

    if (height > maxHeight) {
      height = maxHeight;
      width = Math.round(height * proporcao);
    }

    return { width, height };
  }

  private calcularTamanhoBase64(base64: string): number {
    const pureBase64 = base64.includes(',') ? base64.split(',')[1] : base64;
    const padding = (pureBase64.match(/=+$/) || [''])[0].length;
    return pureBase64.length * 0.75 - padding;
  }
}
